import Image from "next/image";
import Link from "next/link";
import { canOptimizeExploreImage } from "../_lib/explore-image";
import type { ExploreStoryCard } from "../types";
import { STORY_BADGE_STYLES } from "./category-badge-styles";

const MATERIAL_TYPE_LABELS: Partial<
  Record<ExploreStoryCard["materialType"], string>
> = {
  PROMPT: "Prompt",
  WORKFLOW: "Workflow",
  RELEASE: "Release",
  DEAL: "Deal",
};

interface FreshStoryCompactItemProps {
  story: ExploreStoryCard;
  exploreBase: string;
  imageSizes?: string;
  badgeLabel?: string | null;
  prefetch?: boolean;
}

/**
 * Vertical grid card for the top row of Fresh Stories.
 * Matches editorial reference: generous spacing, headline + subtitle,
 * image rounded at top only.
 *
 * Spacing hierarchy (inner/outer):
 *   grid gap          20 px  (parent gap-5)
 *   image inset       12 px  (mx-3 mt-3)
 *   content padding   16 px  (px-4 pb-4)
 *   headline→summary   6 px  (mb-1.5)
 *   summary→tags      14 px  (mb-3.5)
 */
export function FreshStoryCompactItem({
  story,
  exploreBase,
  imageSizes = "(max-width: 1023px) 33vw, 302px",
  badgeLabel,
  prefetch,
}: FreshStoryCompactItemProps) {
  const primaryTag = story.productTags[0] ?? story.topicTags[0];
  const resolvedBadgeLabel =
    badgeLabel ?? MATERIAL_TYPE_LABELS[story.materialType];
  const badgeStyleClass = STORY_BADGE_STYLES[story.storyBadge];
  const shouldOptimizeImage = story.coverImageUrl
    ? canOptimizeExploreImage({ src: story.coverImageUrl })
    : false;

  return (
    <Link
      className="group hover:-translate-y-1.5 flex h-full flex-col overflow-hidden rounded-2xl border border-[var(--explore-card-border)] bg-[var(--explore-bg-card)] no-underline shadow-[var(--explore-card-shadow)] transition-[transform,box-shadow] duration-300 ease-out hover:shadow-[var(--explore-card-shadow-hover)] active:scale-[0.98]"
      href={`${exploreBase}/stories/${story.storyId}`}
      prefetch={prefetch}
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
    >
      {/* Image — 16:9, generous inset, rounded top only */}
      <div className="relative mx-2 mt-2 aspect-[16/9] overflow-hidden rounded-t-xl bg-gradient-to-br from-[var(--explore-tag-bg)] to-[var(--explore-border-light)]">
        {story.coverImageUrl && (
          <Image
            alt={story.headline}
            className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
            fill
            sizes={imageSizes}
            src={story.coverImageUrl}
            unoptimized={!shouldOptimizeImage}
          />
        )}

        {/* New badge */}
        {story.isNew && (
          <div className="absolute top-2.5 left-2.5 z-10 flex items-center gap-1.5 rounded-full bg-[var(--explore-badge-overlay-bg)] px-2.5 py-1 shadow-[var(--explore-card-shadow)] backdrop-blur-[10px]">
            <span className="h-2 w-2 animate-[explore-pulse-dot_1.5s_ease-in-out_infinite] rounded-full bg-[var(--explore-new-indicator)] shadow-[0_0_0_3px_var(--explore-new-indicator-ring)]" />
            <span className="font-bold text-[0.65rem] text-[var(--explore-text-primary)] uppercase tracking-[0.03em]">
              New
            </span>
            <span className="h-3 w-px bg-[var(--explore-border)]" />
            <span className="font-medium text-[0.6rem] text-[var(--explore-text-muted)]">
              {story.timeAgo}
            </span>
          </div>
        )}
      </div>

      {/* Content — generous padding to breathe */}
      <div className="flex flex-1 flex-col px-4 pt-3.5 pb-4">
        <h3
          className="mb-1.5 line-clamp-2 text-pretty font-semibold text-[1.3rem] text-[var(--explore-text-primary)] leading-[1.3]"
          style={{ fontFamily: "var(--explore-card-title-stack)" }}
        >
          {story.headline}
        </h3>

        <p className="mb-3.5 line-clamp-2 text-[0.85rem] text-[var(--explore-text-secondary)] leading-[1.5]">
          {story.summary}
        </p>

        <div className="mt-auto flex flex-wrap items-center gap-1.5">
          {resolvedBadgeLabel && (
            <span
              className={`inline-flex items-center rounded-sm px-2 py-0.5 font-bold text-[0.6rem] uppercase tracking-[0.05em] ${badgeStyleClass}`}
            >
              {resolvedBadgeLabel}
            </span>
          )}
          {story.categoryEmoji && (
            <span className="inline-flex items-center justify-center rounded-sm bg-[var(--explore-tag-bg)] px-1.5 py-0.5 text-[0.75rem]">
              {story.categoryEmoji}
            </span>
          )}
          {primaryTag && (
            <span className="inline-flex items-center rounded-sm bg-[var(--explore-tag-bg)] px-2 py-0.5 font-medium text-[0.72rem] text-[var(--explore-tag-text)]">
              {primaryTag.displayName}
            </span>
          )}
          <span className="text-[0.72rem] text-[var(--explore-text-muted)]">
            {story.timeAgo}
          </span>
        </div>
      </div>
    </Link>
  );
}
